// API Service Configuration Interface
export interface ApiServiceConfig {
  key: string; // Service identifier, e.g., 'member', 'auth', 'console', 'billing'
  name: string; // Service name
  baseURL: string; // Base URL
  useLocal: boolean; // Whether to use local service
  localPort?: number; // Local service port number
  timeout?: number; // Request timeout
  description?: string; // Service description
}

// RequestConfig Interface for configuring HTTP requests
export interface RequestConfig {
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  timeout?: number;
  withCredentials?: boolean;
}

export interface ApiResponseHeader {
  resultCode: number;
  resultMessage: string;
  isSuccessful: boolean;
}

export interface ApiSingleResult<T> {
  data: T;
  content?: T;
}

export interface ApiListResult<T> {
  data: T[];
  contents?: T[];
}

export interface Pagination {
  total: number;
  pages: number;
  pageNum: number;
  pageSize: number;
}

export interface ApiPagedListResult<T> extends Pagination {
  data: T[];
  contents?: T[];
}

export interface ApiResponse<T> {
  header: ApiResponseHeader;
  result: ApiSingleResult<T> | ApiListResult<T> | ApiPagedListResult<T>;
}

export interface ApiSingleResponse<T> {
  header: ApiResponseHeader;
  result: ApiSingleResult<T>;
}

export interface ApiListResponse<T> {
  header: ApiResponseHeader;
  result: ApiListResult<T>;
}

export interface ApiPagedListResponse<T> {
  header: ApiResponseHeader;
  result: ApiPagedListResult<T>;
}

export interface PaginationSearch {
  page: number;
  size: number;
}

export interface LegacyApiResponse<T> {
  code: number;
  message?: string;
  data: T;
}

export interface ApiRes<T> {
  code: number;
  message?: string;
  data: T;
}

export function normalizeResponse<T>(raw: ApiResponse<T>): ApiRes<T> {
  return {
    code: raw.header?.resultCode,
    message: raw.header?.resultMessage ?? '',
    data: raw.result?.data as T,
  };
}

export interface FileUploadResponse<T> {
  header: ApiResponseHeader;
  result: ApiSingleResult<T>;
}

export function normalizeFileResponse<T>(
  raw: FileUploadResponse<T>
): ApiRes<T> {
  return {
    code: raw.header?.isSuccessful ? 200 : raw.header?.resultCode,
    message: raw.header?.resultMessage ?? '',
    data: raw.result?.content as T,
  };
}

export const SUCCESS_CODE = 200;
